import type { ReactNode } from 'react'
import type { Address } from '../store/model/address.model'
import type { Either } from '../helpers/types'
import type { AgentWidgetMode } from './agent-mode'

/** Opt-in host transport for bounded application-local tools (MCP-style). */
export type MobileMcpToolBridge = {
  send: (request: {
    prompt: string
    agentId?: string
    chatId?: string
    signal: AbortSignal
    /** Complete accumulated assistant text, not a delta or a tool plan. */
    onText: (text: string) => void
  }) => Promise<{ text: string; chatId?: string; metadata?: Record<string, unknown> }>
  reset?: () => void
  renderRecommendations?: (metadata: Record<string, unknown>) => ReactNode
}

export type SpeechOrbMountArgs = {
  container: HTMLElement
  mode: 'speech'
  instanceId?: string
  agentId?: string
  agentName?: string
  primaryColor?: string
  onClose?: () => void
}

export type SpeechOrbAdapter = {
  mount: (
    args: SpeechOrbMountArgs
  ) => void | (() => void) | Promise<void | (() => void)>
  unmount?: (container: HTMLElement) => void
}

export type WidgetMountTarget = string | HTMLElement

export type WidgetPlacement = {
  position?: 'fixed' | 'absolute'
  top?: string
  right?: string
  bottom?: string
  left?: string
  zIndex?: number | string
}

export type PageNarrationConfig = {
  enabled: boolean
}

export type AgentOutputType = 'chat' | 'code' | 'image' | 'video' | 'speech' | 'search'

export type AgentOutputEvent = {
  type: AgentOutputType
  mode?: AgentWidgetMode
  agentId?: string
  instanceId?: string
  output: unknown
  timestamp?: string
}

export type AgentCloseEvent = {
  reason: string
  mode?: AgentWidgetMode
  agentId?: string
  instanceId?: string
  timestamp?: string
}

/**
 * Public config type for the widget.
 * Uses Either<> to ensure callers provide either AppConfig or LocalConfig (but not both).
 */
export type LibConfig = Either<AppConfig, LocalConfig>

/**
 * App-backed (agent) mode config
 */
export type AppConfig = {
  agentId: string
  baseApiUrl?: string
  mode?: AgentWidgetMode
  searchPlaceholder?: string
  instanceId?: string
  mountTo?: WidgetMountTarget
  placement?: WidgetPlacement
  onOutput?: (event: AgentOutputEvent) => void
  onClose?: (event: AgentCloseEvent) => void
  speechOrb?: SpeechOrbAdapter
  speechOrbGlobal?: string
  pageNarration?: PageNarrationConfig
  mobileMcpToolBridge?: MobileMcpToolBridge
  fullscreenChat?: boolean
}

/**
 * Local/static mode config
 */
export type LocalConfig = {
  name: string
  mode?: AgentWidgetMode
  searchPlaceholder?: string
  instanceId?: string
  mountTo?: WidgetMountTarget
  placement?: WidgetPlacement
  onOutput?: (event: AgentOutputEvent) => void
  onClose?: (event: AgentCloseEvent) => void
  speechOrb?: SpeechOrbAdapter
  speechOrbGlobal?: string
  pageNarration?: PageNarrationConfig
  mobileMcpToolBridge?: MobileMcpToolBridge
  fullscreenChat?: boolean
  companyName?: string
  companyIconUrl?: string
  customColor?: string
  description?: string
  phoneNumber?: string
  docUrl?: string
  apiDocUrl?: string
  email?: string
  schedule?: string
  address?: Omit<Address, 'id'>
  showMap?: boolean
  googleMapApiKey?: string
  support?: {
    type: 'page' | 'email'
    value: string
  }
  greeting?: string
  customOptions?: CustomOption[]
}

export type CustomOption = {
  icon: string
  label: string
  link: string
  openNewTab: boolean
}

// -----------------------------
// ✅ Constants
// -----------------------------
export const DEFAULT_BASE_API_URL = 'https://osirus.ai/api'

// -----------------------------
// ✅ Runtime type guards
// -----------------------------
export function isAppConfig(config: unknown): config is AppConfig {
  return (
    !!config &&
    typeof config === 'object' &&
    typeof (config as any).agentId === 'string' &&
    (config as any).agentId.length > 0
  )
}

export function isLocalConfig(config: unknown): config is LocalConfig {
  return (
    !!config &&
    typeof config === 'object' &&
    typeof (config as any).name === 'string' &&
    (config as any).name.length > 0
  )
}

/**
 * Optional helper: normalize arbitrary input into a LibConfig-like shape.
 * NOTE: This does not guarantee Either<> exclusivity at runtime.
 */
export function normalizeLibConfig(input: unknown): Partial<LibConfig> {
  if (!input || typeof input !== 'object') return {}
  const cfg = input as any
  const bridge = cfg.mobileMcpToolBridge
  const chatOptions = {
    fullscreenChat: cfg.fullscreenChat === true,
    mobileMcpToolBridge: bridge && typeof bridge.send === 'function' ? bridge : undefined,
  }

  if (typeof cfg.agentId === 'string') {
    return {
      ...chatOptions,
      agentId: cfg.agentId,
      baseApiUrl:
        typeof cfg.baseApiUrl === 'string' && cfg.baseApiUrl.trim().length > 0
          ? cfg.baseApiUrl
          : DEFAULT_BASE_API_URL,
      mode: typeof cfg.mode === 'string' ? cfg.mode : undefined,
      searchPlaceholder:
        typeof cfg.searchPlaceholder === 'string' ? cfg.searchPlaceholder : undefined,
      instanceId:
        typeof cfg.instanceId === 'string' ? cfg.instanceId : undefined,
      mountTo:
        typeof cfg.mountTo === 'string' ||
        (typeof HTMLElement !== 'undefined' && cfg.mountTo instanceof HTMLElement)
          ? cfg.mountTo
          : undefined,
      placement:
        cfg.placement && typeof cfg.placement === 'object' ? cfg.placement : undefined,
      onOutput: typeof cfg.onOutput === 'function' ? cfg.onOutput : undefined,
      onClose: typeof cfg.onClose === 'function' ? cfg.onClose : undefined,
      speechOrb:
        cfg.speechOrb && typeof cfg.speechOrb === 'object' ? cfg.speechOrb : undefined,
      speechOrbGlobal:
        typeof cfg.speechOrbGlobal === 'string' ? cfg.speechOrbGlobal : undefined,
      pageNarration:
        cfg.pageNarration && typeof cfg.pageNarration.enabled === 'boolean'
          ? { enabled: cfg.pageNarration.enabled }
          : undefined,
    } as Partial<LibConfig>
  }

  return {
    ...chatOptions,
    name: typeof cfg.name === 'string' ? cfg.name : '',
    mode: typeof cfg.mode === 'string' ? cfg.mode : undefined,
    searchPlaceholder:
      typeof cfg.searchPlaceholder === 'string' ? cfg.searchPlaceholder : undefined,
    instanceId:
      typeof cfg.instanceId === 'string' ? cfg.instanceId : undefined,
    mountTo:
      typeof cfg.mountTo === 'string' ||
      (typeof HTMLElement !== 'undefined' && cfg.mountTo instanceof HTMLElement)
        ? cfg.mountTo
        : undefined,
    placement:
      cfg.placement && typeof cfg.placement === 'object' ? cfg.placement : undefined,
    onOutput: typeof cfg.onOutput === 'function' ? cfg.onOutput : undefined,
    onClose: typeof cfg.onClose === 'function' ? cfg.onClose : undefined,
    speechOrb:
      cfg.speechOrb && typeof cfg.speechOrb === 'object' ? cfg.speechOrb : undefined,
    speechOrbGlobal:
      typeof cfg.speechOrbGlobal === 'string' ? cfg.speechOrbGlobal : undefined,
    pageNarration:
      cfg.pageNarration && typeof cfg.pageNarration.enabled === 'boolean'
        ? { enabled: cfg.pageNarration.enabled }
        : undefined,
    companyName: typeof cfg.companyName === 'string' ? cfg.companyName : undefined,
    companyIconUrl:
      typeof cfg.companyIconUrl === 'string' ? cfg.companyIconUrl : undefined,
    customColor: typeof cfg.customColor === 'string' ? cfg.customColor : undefined,
    description: typeof cfg.description === 'string' ? cfg.description : undefined,
    phoneNumber: typeof cfg.phoneNumber === 'string' ? cfg.phoneNumber : undefined,
    docUrl: typeof cfg.docUrl === 'string' ? cfg.docUrl : undefined,
    apiDocUrl: typeof cfg.apiDocUrl === 'string' ? cfg.apiDocUrl : undefined,
    email: typeof cfg.email === 'string' ? cfg.email : undefined,
    schedule: typeof cfg.schedule === 'string' ? cfg.schedule : undefined,
    address: cfg.address,
    showMap: typeof cfg.showMap === 'boolean' ? cfg.showMap : undefined,
    googleMapApiKey:
      typeof cfg.googleMapApiKey === 'string' ? cfg.googleMapApiKey : undefined,
    support:
      cfg.support && typeof cfg.support === 'object'
        ? { type: cfg.support.type, value: cfg.support.value }
        : undefined,
    greeting: typeof cfg.greeting === 'string' ? cfg.greeting : undefined,
    customOptions: Array.isArray(cfg.customOptions) ? cfg.customOptions : undefined,
  } as Partial<LibConfig>
}

// -----------------------------
// ✅ Browser-safe defaults (no process.env)
// -----------------------------
declare global {
  interface Window {
    __OSIRUS_BOT_ID?: string
    __OSIRUS_BASE_API_URL?: string
  }
}

function readWindowString(key: keyof Window): string {
  if (typeof window === 'undefined') return ''
  const v = (window as any)[key]
  return typeof v === 'string' ? v : ''
}

function readDefineString(name: string): string {
  // Avoid ReferenceError by using globalThis lookup
  const v = (globalThis as any)?.[name]
  return typeof v === 'string' ? v : ''
}

declare const __OSIRUS_BOT_ID__: string
declare const __OSIRUS_BASE_API_URL__: string

const defaultAgentId =
  (typeof __OSIRUS_BOT_ID__ !== 'undefined' ? __OSIRUS_BOT_ID__ : '') ||
  readWindowString('__OSIRUS_BOT_ID')

const defaultBaseApiUrl =
  (typeof __OSIRUS_BASE_API_URL__ !== 'undefined'
    ? __OSIRUS_BASE_API_URL__
    : '') ||
  readWindowString('__OSIRUS_BASE_API_URL') ||
  DEFAULT_BASE_API_URL

export const DefaultConfig: LibConfig = {
  agentId: defaultAgentId, // can be empty; init() should provide real agentId
  baseApiUrl: defaultBaseApiUrl,
}
